import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material/styles';
import {
    Alert,
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CollapsedBreadcrumbs from 'common/components/CollapsedBreadcrumbs';
import { getBreadcrumbs } from 'common/components/segment/helpers';
import { PROPOSAL_STATUS } from 'common/constants';

import TitlePreview from './TitlePreview';

const HeaderPreview = ({
    commercialOfferId,
    currentBreadcrumb,
    generateProposal,
    handleClickBreadcrumb,
    handleClickEdit,
    isDrawingMode,
    offerInfoPatern,
    proposalId,
    redirect,
    renderStatus,
    setDrawingMode,
    showGenerateProposal = false,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const breadCrumbs = getBreadcrumbs(handleClickBreadcrumb, offerInfoPatern);

    const renderGenerateProposalButtons = () => {
        if (!showGenerateProposal) return null;
        if (renderStatus === PROPOSAL_STATUS.GENERATED) {
            return (
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    <Button
                        fullWidth
                        onClick={() =>
                            redirect?.(`/panel-layout/preview/${proposalId}`)
                        }
                        size="large"
                    >
                        {t('Preview')}
                    </Button>

                    <Button
                        fullWidth
                        endIcon={<EditIcon />}
                        onClick={() =>
                            handleClickEdit?.(commercialOfferId, proposalId)
                        }
                        size="large"
                        variant="outlined"
                    >
                        {t('Edit')}
                    </Button>
                </Box>
            );
        }
        return (
            <Button
                fullWidth
                onClick={() => generateProposal?.(proposalId)}
                size="large"
            >
                {t('Generate report')}
            </Button>
        );
    };

    return (
        <Box>
            {!isDrawingMode && (
                <Box
                    sx={{
                        p: 2,
                        paddingTop: showGenerateProposal ? 0 : 2,
                        paddingRight: { xs: 2, md: 0 },
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', md: 'flex-start' },
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            paddingTop: showGenerateProposal ? 2 : 0,
                        }}
                    >
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 2,
                                mb: 1,
                            }}
                        >
                            <TitlePreview name={offerInfoPatern?.projectName} />
                            <Button
                                onClick={() =>
                                    handleClickBreadcrumb(
                                        offerInfoPatern.contactId,
                                        offerInfoPatern.projectId,
                                    )
                                }
                                size="small"
                                startIcon={<ArrowBackIcon fontSize="small" />}
                                variant="text"
                            >
                                {t('Back')}
                            </Button>
                        </Box>
                        <CollapsedBreadcrumbs
                            current={currentBreadcrumb}
                            locations={breadCrumbs}
                        />
                    </Box>

                    {showGenerateProposal && (
                        <Box sx={{ p: 1, width: { xs: '100%', md: 330 } }}>
                            {renderGenerateProposalButtons()}
                        </Box>
                    )}
                </Box>
            )}

            {isDrawingMode && (
                <Grid
                    container
                    spacing={0}
                    sx={{ alignItems: 'center', px: 2, py: 1 }}
                >
                    <Grid size="grow">
                        <Alert
                            severity="info"
                            action={
                                <IconButton
                                    onClick={() => {
                                        setDrawingMode(false);
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            }
                            sx={{
                                background: isDarkMode
                                    ? '#1B1C20'
                                    : 'rgb(218, 221, 234)',
                                '& .MuiAlert-icon': {
                                    color: '#737fb9',
                                },
                                color: isDarkMode ? '#fff' : 'inherit',
                                mb: 0,
                            }}
                        >
                            <Typography variant="body2" fontWeight="bold">
                                {t('Drawing mode activated')} (
                                {t('Press Escape key to exit')})
                            </Typography>
                            <Typography
                                sx={{ display: { md: 'block', xs: 'none' } }}
                                variant="caption"
                            >
                                {t(
                                    'Plot the points of the area where you need to place panels',
                                )}
                                .
                            </Typography>
                        </Alert>
                    </Grid>
                </Grid>
            )}

            <Divider sx={{ mb: 0 }} />
        </Box>
    );
};

HeaderPreview.propTypes = {
    commercialOfferId: PropTypes.string,
    currentBreadcrumb: PropTypes.string,
    generateProposal: PropTypes.func,
    handleClickBreadcrumb: PropTypes.func,
    handleClickEdit: PropTypes.func,
    isDrawingMode: PropTypes.bool,
    offerInfoPatern: PropTypes.object,
    proposalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    redirect: PropTypes.func,
    renderStatus: PropTypes.string,
    setDrawingMode: PropTypes.func,
    showGenerateProposal: PropTypes.bool,
};

export default HeaderPreview;
